import { Button } from '@app/components/core/button/Button'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import {
  faLocationDot,
  faUserGroup,
  faSquareCheck,
  faCube,
} from '@fortawesome/pro-light-svg-icons'
import { faPlus, faCheck, faShare } from '@fortawesome/pro-regular-svg-icons'
import { i18n } from '@i18n/index'
import { useLanguage } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { Card } from '@app/components/blocks/card/Card'
import useMediaQuery from '@app/util/useMediaQuery'
import { Modal } from '@app/components/blocks/modal/Modal'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { useState, useMemo } from 'react'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'
import { useAuthState, useClient } from '@based/sdk/react'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { enrich, pluralize } from '@i18n/localizeHelper'
import { INITIATIVE_USER_ROLES, InitiativeUserRole } from '@schema/initiative'
import { ViewInitiativeModel } from 'functions/app/initiative/view'
import { addressBuilder } from '@app/util/helpers'
import {
  IconList,
  IconListItem,
} from '@app/components/blocks/iconList/IconList'
import { Tooltip } from '@app/components/core/tooltip/Tooltip'

export const DataCard = ({
  initiative,
}: {
  initiative: ViewInitiativeModel
}) => {
  const isMobile = useMediaQuery()
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.initiativePage
  const [joinInitiativeModal, setJoinInitiativeModal] = useState(false)
  const [joinInitiativeModalText, setJoinInitiativeModalText] = useState('')
  const [joinInitiativeModalTitle, setJoinInitiativeModalTitle] = useState('')
  const [loading, setLoading] = useState(false)
  const [shareInitiativeModal, setShareInitiativeModal] = useState(false)
  const client = useClient()
  const { withAuthCheck } = useAuthModal()
  const authState = useAuthState()
  const isMember = isUserMember(initiative, String(authState.userId ?? ''))
  const isApplicant = isUserApplicant(
    initiative,
    String(authState.userId ?? ''),
  )

  const location = initiative.location.address.locality
    ? addressBuilder({
        postalCode: initiative.location.address.postalCode,
        locality: initiative.location.address.locality,
      })
    : initiative.location.address.municipality
      ? addressBuilder({
          postalCode: initiative.location.address.postalCode,
          municipality: initiative.location.address.municipality,
        })
      : initiative.location.address.region
        ? addressBuilder({
            postalCode: initiative.location.address.postalCode,
            region: initiative.location.address.region,
          })
        : translations.unknownLocation

  const onApply = () => {
    const modalText = isMember
      ? enrich(translations.modalLeaveConfirmation, initiative.title)
      : isApplicant
        ? enrich(translations.modalWithdrawConfirmation, initiative.title)
        : enrich(translations.modalApplyConfirmation, initiative.title)
    const modalTitle = isMember
      ? translations.modalDefaultTitle
      : isApplicant
        ? translations.modalDefaultTitle
        : translations.modalApplyTitle
    setJoinInitiativeModalText(modalText)
    setJoinInitiativeModalTitle(modalTitle)
    setJoinInitiativeModal(true)
  }

  const onShare = () => setShareInitiativeModal(true)

  const followInitiative = async () => {
    setLoading(true)
    try {
      await withAuthCheck(async () => {
        const isFollowing = doesUserFollow(
          initiative,
          String(authState.userId ?? ''),
        )
        if (isFollowing) {
          await client.call('unfollow-initiative', {
            initiativeId: initiative.id,
          })
        } else {
          await client.call('follow-initiative', {
            initiativeId: initiative.id,
          })
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const joinInitiative = async () => {
    setLoading(true)
    try {
      await withAuthCheck(async () => {
        let result: unknown
        if (isMember || isApplicant) {
          result = await client.call('leave-initiative', {
            initiativeId: initiative.id,
          })
        } else {
          result = await client.call('apply-to-initiative', {
            initiativeId: initiative.id,
          })
        }
        if (result) {
          setJoinInitiativeModal(false)
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const followers = initiative.users.filter((u) => u.$role === 'follower')

  const listItems = useMemo<IconListItem[]>(
    () => [
      { icon: faLocationDot, text: location },
      {
        icon: faUserGroup,
        text: pluralize(translations.follower, followers.length),
      },
      {
        icon: faCube,
        text: initiative.developmentForm?.name ?? translations.notSpecified,
      },
      {
        icon: faSquareCheck,
        text: initiative.phase?.name ?? translations.notSpecified,
      },
    ],
    [
      location,
      translations,
      followers.length,
      initiative.developmentForm?.name,
      initiative.phase?.name,
    ],
  )

  return (
    <>
      <Card
        style={{
          height: isMobile ? 'auto' : '27rem', // TODO: this is to aligh it with gallery, but maybe instead the whole page should be flex grid
          padding: 0,
        }}
        contentStyle={{
          padding: '2.5rem 1.5rem 2rem 1.5rem',
        }}
      >
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ height: '100%', justifyContent: 'space-between' }}
        >
          <IconList items={listItems} style={{ marginBottom: '1.5rem' }} />
          <CardContent
            initiative={initiative}
            onApply={onApply}
            onFollow={() => void followInitiative()}
            onShare={onShare}
          />
        </Column>
      </Card>
      <Modal
        isOpen={joinInitiativeModal}
        onClose={() => setJoinInitiativeModal(false)}
        onConfirm={() => void joinInitiative()}
        loading={loading}
        title={joinInitiativeModalTitle}
      >
        <Text.Medium style={{ lineHeight: '1.75rem' }}>
          <ParagraphedText>{joinInitiativeModalText}</ParagraphedText>
        </Text.Medium>
      </Modal>
      <SocialShareModal
        isOpen={shareInitiativeModal}
        onClose={() => setShareInitiativeModal(false)}
        title={initiative.title}
      />
    </>
  )
}

const CardContent = ({
  initiative,
  onApply,
  onFollow,
  onShare,
}: {
  initiative: ViewInitiativeModel
  onApply: VoidFunction
  onFollow: VoidFunction
  onShare: VoidFunction
}) => {
  const isMobile = useMediaQuery()
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.initiativePage
  const authState = useAuthState()
  const isMember = isUserMember(initiative, String(authState.userId ?? ''))
  const isApplicant = isUserApplicant(
    initiative,
    String(authState.userId ?? ''),
  )
  const showFollowButton = !isMember && !isApplicant
  return (
    <Column
      gap="1.5rem"
      alignItems="stretch"
      style={{ marginTop: '0.5rem', flexGrow: 0 }}
    >
      <Tooltip content={translations.applyTooltip}>
        <Button
          style={{ width: '100%' }}
          disabled={isUserOwner(initiative, String(authState.userId ?? ''))}
          label={
            isMember
              ? translations.leave
              : isApplicant
                ? translations.withdrawApplication
                : translations.apply
          }
          onClick={onApply}
        />
      </Tooltip>
      <Row style={{ justifyContent: 'stretch' }}>
        {showFollowButton && (
          <Tooltip
            content={translations.followTooltip}
            containerStyle={{ flex: '1 0 0' }}
          >
            <Button
              variant="secondary"
              label={
                doesUserFollow(initiative, String(authState.userId ?? ''))
                  ? translations.following
                  : translations.follow
              }
              icon={
                doesUserFollow(initiative, String(authState.userId ?? ''))
                  ? faCheck
                  : faPlus
              }
              iconPosition="left"
              size={isMobile ? 'small' : 'medium'}
              onClick={onFollow}
            />
          </Tooltip>
        )}
        <Button
          variant="secondary"
          label={translations.share}
          icon={faShare}
          iconPosition="left"
          size={isMobile ? 'small' : 'medium'}
          style={{ flex: '1 0 0' }}
          onClick={onShare}
        />
      </Row>
    </Column>
  )
}

const memberRoles: InitiativeUserRole[] = [
  INITIATIVE_USER_ROLES.owner,
  INITIATIVE_USER_ROLES.admin,
  INITIATIVE_USER_ROLES.member,
]

const isUserOwner = (initiative: ViewInitiativeModel, userId: string) =>
  initiative.users.some(
    (u) => u.id === Number(userId) && u.$role === INITIATIVE_USER_ROLES.owner,
  )

const isUserMember = (initiative: ViewInitiativeModel, userId: string) =>
  initiative.users.some(
    (u) => u.id === Number(userId) && memberRoles.includes(u.$role),
  )

const isUserApplicant = (initiative: ViewInitiativeModel, userId: string) =>
  initiative.users.some(
    (u) =>
      u.id === Number(userId) && u.$role === INITIATIVE_USER_ROLES.applicant,
  )

const doesUserFollow = (initiative: ViewInitiativeModel, userId: string) =>
  initiative.users.some(
    (u) =>
      u.id === Number(userId) && u.$role === INITIATIVE_USER_ROLES.follower,
  )
